/**
 * The one number formatter for web copy (screens §1.4). Units come from CP-1 `KPI_DEFS`; the arithmetic is `@gxs/metric-defs`.
 * Rates are decimals and are multiplied by 100 only inside `formatMetric`.
 */
import { KPI_DEFS, type KpiId } from "@bsim/domain";
import type { ContentCatalog } from "@bsim/contracts";
import { changeScaleOf, formatMetric, formatRateChange, printedPlaces, printedSum, printedValue, TWO_DP, type ChangeScale } from "@gxs/metric-defs";

/** NIM, NCO and CET1 read to 2 dp (§1.4); the other capital ratios and the return the price implies follow them. */
// 2 dp for tier1, trbc and leverage — §1.4 names only NIM, NCO, CET1 and COE, and the mockup shows TRBC as 13.94%.
// The other rates that sit near 1% (ROA, deposit costs, NPL, reserve ratio) read to 2 dp as well, so a table of margin, loss and capital measures
// never mixes 1.8% beside 3.64% — a 1 dp rate hides moves of a few basis points on a measure that small; the share and growth rates stay 1 dp.

/** Growth and change rates: a growth rate always carries its sign (levels never do); pass `signed: false` to print one bare. */
const GROWTH: ReadonlySet<KpiId> = new Set(["loanGrowth", "depositGrowth", "creGrowth36m", "feeGrowth", "coreRevenueGrowth"]);

export function formatKpi(kpi: KpiId, value: number, opts: { readonly signed?: boolean } = {}): string {
  const precision = TWO_DP.has(kpi) ? 2 : undefined;
  const signed = opts.signed ?? GROWTH.has(kpi);
  return formatMetric(value, KPI_DEFS[kpi].unit, { ...(precision === undefined ? {} : { precision }), ...(signed ? { signed: true } : {}) });
}

/** The KPI as `formatKpi` prints it, so a limit test reads what the player reads: a reading is past its limit only when the printed figures say so (−20.0% beside a −20.0% limit is not a breach). */
export const printedKpi = (kpi: KpiId, value: number): number => printedValue(value, KPI_DEFS[kpi].unit, TWO_DP.has(kpi) ? 2 : undefined);

/**
 * The difference of two printed levels, in the KPI's change unit, so a reader who subtracts the two printed figures gets the printed difference (a minus b).
 * Basis-point KPIs: whole bp of the printed 2 dp levels ("0.47%" vs "0.48%": "\u22121bp"). Other rates: points, at the places the levels print
 * ("15.5%" vs "15.6%": "\u22120.1 pts"; 2 dp levels: 2 dp). `same` is true when the printed difference is zero, and the figure is then "0". Other units: the printed values subtracted.
 * PROPOSAL (OR-30, open to the owner's veto): a difference between two 1 dp levels prints at 1 dp, because two-decimal points over one-decimal levels claim a precision the levels do not show;
 * the two-decimal rule stays for points over 2 dp levels. `words` writes "points" for "pts"; `signed: false` prints the size alone, for "1.9 points past".
 */
export interface PrintedDifference { readonly text: string; readonly same: boolean; readonly value: number }
export function printedDifference(kpi: KpiId, a: number, b: number, opts: { readonly signed?: boolean; readonly words?: boolean } = {}): PrintedDifference {
  const signed = opts.signed ?? true;
  const mag = (n: number): number => (signed ? n : Math.abs(n));
  const unit = KPI_DEFS[kpi].unit;
  if (unit !== "rate") {
    const diff = printedKpi(kpi, a) - printedKpi(kpi, b);
    const text = formatKpi(kpi, mag(diff), { signed });
    return { text, same: !/[1-9]/.test(text), value: diff };
  }
  const dp = printedPlaces(kpi);
  const units = Math.round(printedKpi(kpi, a) * 100 * 10 ** dp) - Math.round(printedKpi(kpi, b) * 100 * 10 ** dp);
  return { text: rateStepText(kpi, units, dp, signed, opts.words === true), same: units === 0, value: (units * 10 ** (2 - dp)) / 10_000 };
}

export { printedPlaces };

/** A change of `units` steps of 10^-dp percentage points, in the KPI's own change scale; a zero prints in that scale too ("0bp" for a margin, "0.0 pts" for a ratio), never in the other. */
function rateStepText(kpi: KpiId, units: number, dp: number, signed: boolean, words: boolean): string {
  const bp = units * 10 ** (2 - dp);
  if (changeScaleOf(kpi) === "bp" && Math.abs(bp) < 1000) return formatMetric(signed ? bp : Math.abs(bp), "bp", { signed });
  return `${formatMetric((signed ? units : Math.abs(units)) / 10 ** dp, "index", { precision: dp, signed })} ${words ? "points" : "pts"}`;
}

/**
 * A change in a KPI as the printed levels can show it: a rate rounds to the places its levels print at (whole bp for the margin, 0.1 point for a ratio at one decimal), so
 * parts rounded this way add up beside the levels. Other units print as `formatKpiChange` does. A zero prints in the KPI's own scale.
 */
export function formatPrintedChange(kpi: KpiId, delta: number, opts: { readonly signed?: boolean; readonly words?: boolean } = {}): string {
  if (KPI_DEFS[kpi].unit !== "rate") return formatKpi(kpi, delta, { signed: opts.signed ?? true });
  const dp = printedPlaces(kpi);
  return rateStepText(kpi, Math.round(delta * 100 * 10 ** dp), dp, opts.signed ?? true, opts.words === true);
}

/** The size of one printed step of a KPI's change, in the unit the KPI is stored in (a decimal rate; dollars for a price). */
export const printedStepOf = (kpi: KpiId): number => (KPI_DEFS[kpi].unit === "rate" ? 10 ** -(printedPlaces(kpi) + 2) : 0.01);

/** A KPI the engine has not produced (a growth rate needs a year behind it; an older pack lacks it) reads "—", never 0. */
export const formatKpiOrDash = (kpi: KpiId, value: number | null | undefined, opts: { readonly signed?: boolean } = {}): string =>
  value === null || value === undefined ? "—" : formatKpi(kpi, value, opts);

/** "+$0.55" (the "/share" suffix is the caller's copy). */
export const formatUsdPerShare = (value: number, opts: { readonly signed?: boolean } = {}): string =>
  formatMetric(value, "usdPerShare", opts);

/** A per-share figure said in words, the way copy reads it: "$62.00 a share", "+$0.42 a share". */
export const formatUsdAShare = (value: number, opts: { readonly signed?: boolean } = {}): string => `${formatUsdPerShare(value, opts)} a share`;

/** A level on an index scale, a plain number to `dp` places (default 1): "104.2". */
export const formatIndexLevel = (level: number, dp = 1): string => formatMetric(level, "index", { precision: dp });

/** A factor (a price to book, a multiple of a base) to two places, with a true minus: "1.25x", "+0.18x". */
export const formatFactor = (value: number, opts: { readonly signed?: boolean } = {}): string => formatMetric(value, "multiple", opts);

/** The bare figure of a score or move in points, from the decimal rate, for a cell whose header says "points": "+0.84", "\u22122.10". */
export const formatPointsFigure = (rate: number, opts: { readonly signed?: boolean } = {}): string => formatMetric(rate * 100, "index", { precision: 2, ...opts });

/** Value Created in points from the decimal rate: "+0.84 points" (the screens never print the shorthand "pp"). */
export const formatPp = (rate: number, opts: { readonly signed?: boolean } = {}): string =>
  `${formatMetric(rate * 100, "index", { precision: 2, ...opts })} points`;

/** Whole basis points, true minus: "+6bp". */
export const formatBp = (bp: number, opts: { readonly signed?: boolean } = {}): string =>
  formatMetric(bp, "bp", opts);

/** One rule for every screen and the memo: `changeScaleOf` in `@gxs/metric-defs`. */
export const changeScaleOfKpi = (kpi: KpiId): ChangeScale => changeScaleOf(kpi);

/** A change in a KPI in its own unit: NIM, losses, deposit cost and CET1 in basis points, other ratios in percentage points ("+0.30 points"), anything else signed in its unit ("+$1.40"). */
export const formatKpiChange = (kpi: KpiId, delta: number, opts: { readonly signed?: boolean } = {}): string =>
  KPI_DEFS[kpi].unit === "rate" ? formatRateChange(delta, changeScaleOf(kpi), opts) : formatKpi(kpi, delta, { signed: opts.signed ?? true });

/** A growth or change rate, always signed: "+4.2%", "\u22123.1%" (a level is `formatRate`, unsigned). */
export const formatGrowthRate = (rate: number, dp = 1): string => formatMetric(rate, "rate", { precision: dp, signed: true });

/** A rate printed to `dp` places, the one way a rate prints outside a KPI ("12.5%"). */
export const formatRate = (rate: number, dp: number, opts: { readonly signed?: boolean } = {}): string =>
  formatMetric(rate, "rate", { precision: dp, ...(opts.signed ? { signed: true } : {}) });

/** A move in percentage points from a figure already in points: "+0.84 points" (zero prints unsigned); `short` is "pts" for a cell too narrow for the word, its header saying "points". */
export const formatPoints = (points: number, opts: { readonly dp?: number; readonly signed?: boolean; readonly short?: boolean } = {}): string =>
  `${formatMetric(points, "index", { precision: opts.dp ?? 2, signed: opts.signed ?? true })} ${opts.short === true ? "pts" : "points"}`;

/** A score in points, in words, from the decimal rate: "4.30 points", "+1.75 points" (singular at exactly one). */
export const formatPointsWord = (rate: number, opts: { readonly dp?: number; readonly signed?: boolean } = {}): string =>
  `${formatMetric(rate * 100, "index", { precision: opts.dp ?? 2, ...(opts.signed ? { signed: true } : {}) })} ${Math.abs(rate * 100) === 1 ? "point" : "points"}`;

/** A length of time in years: "4.2 years", or "4.2 yrs" in a table. */
export const formatYears = (years: number, opts: { readonly short?: boolean } = {}): string =>
  `${formatMetric(years, "index", { precision: 1 })} ${opts.short === true ? "yrs" : "years"}`;

/** A multiple of a base: "1.25×" (two places, a trailing zero dropped). */
export const formatMultiple = (multiple: number): string => `${formatMetric(multiple, "index", { precision: 2 }).replace(/0$/, "")}×`;

/** Shares outstanding in millions: "212.4M". */
export const formatSharesM = (shares: number): string => `${formatMetric(shares / 1e6, "index", { precision: 1 })}M`;

/** One rule for a dollar size: $1B and over reads in billions, below it in millions. */
export const formatUsdScaled = (usd: number, opts: { readonly signed?: boolean } = {}): string => (Math.abs(usd) >= 1e9 ? formatUsdB(usd) : formatUsdM(usd, opts));

/** The place a bank is printed at on every screen: the best place of its tied group (`score.sharedRank`), never its own place within the tie; "–" without one. */
export const displayRank = (bank: { readonly rank: number | null; readonly score: { readonly sharedRank?: number | undefined } | null }): number | string => bank.score?.sharedRank ?? bank.rank ?? "–";

/** The date the score counts the bank at: the end of the round's year (ch10: T = the round-end month). */
export const scoringYearLabel = (roundNo: number): string => `end of year ${roundNo}`;

/** The viewer's local clock time, "09:30". */
export const clockTime = (at: Date | string): string => (typeof at === "string" ? new Date(at) : at).toTimeString().slice(0, 5);

/** A wall-clock time the way it is said aloud: "12:05 am", "1:30 pm". */
export const clockTimeWords = (at: Date | string): string => {
  const d = typeof at === "string" ? new Date(at) : at;
  return `${d.getHours() % 12 === 0 ? 12 : d.getHours() % 12}:${String(d.getMinutes()).padStart(2, "0")} ${d.getHours() < 12 ? "am" : "pm"}`;
};

/** A change in a decimal rate, shown in basis points. */
export const rateDeltaBp = (later: number, earlier: number): number => Math.round((later - earlier) * 10_000);

/** Whole dollars as "$1,234M" (statements are read in millions). */
export const formatUsdM = (usd: number, opts: { readonly signed?: boolean } = {}): string =>
  formatMetric(usd / 1e6, "usdM", { precision: 1, ...opts });

/** Whole millions as "$1,234M", for a sentence or a figure outside a table whose header names the unit. */
export const formatUsdMWhole = (usd: number, opts: { readonly signed?: boolean } = {}): string =>
  formatMetric(usd / 1e6, "usdM", { precision: 0, ...opts });

/** Whole millions as a bare "1,234", for a table cell under a "($M)" header. */
export const formatMillionsBare = (usd: number, opts: { readonly signed?: boolean; readonly precision?: number } = {}): string =>
  formatMetric(usd / 1e6, "index", { precision: 0, ...opts });

/** "$1.2B" (a size, never signed). */
export const formatUsdB = (usd: number): string => formatMetric(Math.abs(usd) / 1e9, "usdM", { precision: 1 }).replace(/M$/, "B");

/** "FY" + (fiscalYearAtSimStart + roundNo): round 2 of a program that starts after FY2025 is FY2027. */
export const fiscalYearLabel = (catalog: ContentCatalog, roundNo: number): string => `FY${catalog.fiscalYearAtSimStart + roundNo}`;

/** `fiscalYearLabel` bound to a catalog, for a sentence builder that names years; undefined while the catalog has not loaded (the builder then uses its own plain default). */
export const yearNamer = (catalog: ContentCatalog | undefined): ((roundNo: number) => string) | undefined => (catalog === undefined ? undefined : (n) => fiscalYearLabel(catalog, n));

/** "Q3 FY26" for the quarter ending at absolute month `periodEndMonth` (12 months per round). */
export function quarterLabel(catalog: ContentCatalog, periodEndMonth: number): string {
  const roundNo = Math.ceil(periodEndMonth / 12);
  const quarter = Math.floor(((periodEndMonth - 1) % 12) / 3) + 1;
  return `Q${quarter} ${fiscalYearLabel(catalog, roundNo).replace("FY20", "FY")}`;
}

export function ordinal(n: number): string {
  const rem = n % 100;
  if (rem >= 11 && rem <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10 <= 3 ? n % 10 : 0]}`;
}

/**
 * Each value rounded to `step`, the rounding left over (against the rounded total) given to the largest part, so the printed parts add to the printed total.
 * Works in whole steps, so cents and basis points add exactly.
 */
export const roundedToSum = (values: readonly number[], total: number, step: number): number[] => printedSum(values, total, step).parts;

/** A card or page title named inside a sentence, quoted as a title: `The answers to “The Fed moves 75bp”, compared`. */
export const quotedTitle = (title: string): string => `\u201c${title}\u201d`;
export * from "./basis";
